/**
 * Tagsignalkörper: dieselbe Aussage wie die Lichter, nur bei Tag.
 * Schwarze Körper, bewusst groß gehalten, damit sie auf Entfernung lesbar bleiben.
 */
import {
  BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  OctahedronGeometry,
  PlaneGeometry,
  SphereGeometry,
  Vector3,
} from 'three'
import type { DayShape, DayShapeKind } from '@/core/types'
import type { VesselModel } from './hulls'

/** Senkrechter Abstand zweier Signalkörper an demselben Punkt (Anlage I: mindestens 1,5 m). */
const TIER_SPACING_M = 1.5

export interface RenderShape {
  spec: DayShape
  object: Object3D
  position: Vector3
  geometries: BufferGeometry[]
}

function shapeSize(lengthM: number): number {
  return Math.min(3, Math.max(0.6, lengthM * 0.035))
}

function buildGeometry(kind: DayShapeKind, size: number): BufferGeometry[] {
  switch (kind) {
    case 'ball':
      return [new SphereGeometry(size / 2, 14, 10)]
    case 'cone-up':
      return [new ConeGeometry(size / 2, size, 12)]
    case 'cone-down': {
      const g = new ConeGeometry(size / 2, size, 12)
      g.rotateZ(Math.PI)
      return [g]
    }
    case 'cones-apex': {
      // Regel 26: zwei Kegel mit den Spitzen aneinander. Nicht zu verwechseln mit der
      // Raute: die Kanten treffen sich in der Mitte in einer Spitze, nicht in einer Fläche.
      const upper = new ConeGeometry(size / 2, size * 0.75, 12)
      upper.rotateZ(Math.PI)
      upper.translate(0, size * 0.375, 0)
      const lower = new ConeGeometry(size / 2, size * 0.75, 12)
      lower.translate(0, -size * 0.375, 0)
      return [upper, lower]
    }
    case 'diamond':
      return [new OctahedronGeometry(size / 2, 0)]
    case 'cylinder':
      return [new CylinderGeometry(size / 2, size / 2, size * 1.2, 14)]
    case 'flag-a': {
      // Starre Nachbildung der Flagge A: weißer Mast-seitiger Teil, blauer Schwalbenschwanz.
      const white = new PlaneGeometry(size * 0.5, size)
      white.translate(-size * 0.25, 0, 0)
      const blue = new PlaneGeometry(size * 0.5, size)
      blue.translate(size * 0.25, 0, 0)
      return [white, blue]
    }
  }
}

const BLACK = new MeshStandardMaterial({ color: 0x0b0d10, roughness: 0.95, metalness: 0 })
const WHITE = new MeshStandardMaterial({ color: 0xf2f4f7, roughness: 0.9, metalness: 0, side: 2 })
const BLUE = new MeshStandardMaterial({ color: 0x1f4bd8, roughness: 0.9, metalness: 0, side: 2 })

export function createShapes(model: VesselModel, specs: DayShape[]): RenderShape[] {
  const size = shapeSize(model.lengthM)
  return specs.map((spec) => {
    const position = model.anchors[spec.mount].clone()
    if (spec.tier) position.y += spec.tier * Math.max(TIER_SPACING_M, size * 1.4)
    const group = new Group()
    const geometries = buildGeometry(spec.kind, size)
    geometries.forEach((geometry, index) => {
      const material = spec.kind === 'flag-a' ? (index === 0 ? WHITE : BLUE) : BLACK
      group.add(new Mesh(geometry, material))
    })
    group.position.copy(position)
    model.group.add(group)
    return { spec, object: group, position, geometries }
  })
}

export function setShapesVisible(shapes: RenderShape[], visible: boolean): void {
  for (const shape of shapes) shape.object.visible = visible
}

export function disposeShapes(shapes: RenderShape[]): void {
  for (const shape of shapes) {
    for (const g of shape.geometries) g.dispose()
    shape.object.removeFromParent()
  }
}
